/**
 * The single RTK Query data layer (S01).
 *
 * One `apiSlice` for the whole app — every endpoint is injected into it by its
 * feature module rather than created as a separate API. That is what makes cache
 * tags work across features: an interaction arriving can invalidate a dashboard
 * KPI without either feature knowing about the other.
 */
import { createApi, fetchBaseQuery, type BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { ApiError } from '@inbound/contract';
import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { LOGIN_ROUTE, loginPath } from '../app/login-path.js';

/** Cache tags. Kept in one place so a typo cannot silently break invalidation. */
export const API_TAGS = [
  'Call',
  'Interaction',
  'Agent',
  'Greeting',
  'Setting',
  'Notification',
  'Health',
  'Allowlist',
  'Me',
] as const;

export type ApiTag = (typeof API_TAGS)[number];

/** A normalized error the UI can render without reaching into transport details. */
export interface NormalizedError {
  status: number | 'FETCH_ERROR' | 'PARSING_ERROR' | 'TIMEOUT_ERROR' | 'CUSTOM_ERROR';
  code: string;
  message: string;
  details?: { path: string; message: string }[];
  requestId?: string;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/api',
  // Sends the httpOnly session cookie. There is no Authorization header to set:
  // the browser never holds a token (S02).
  credentials: 'include',
});

/**
 * Endpoints that must never trigger the refresh-and-retry dance.
 *
 * Refreshing in response to a failed refresh is an infinite loop, and retrying a
 * logout that already cleared the session is pointless.
 */
const NO_REFRESH = ['auth/refresh', 'auth/logout'];

/**
 * A single in-flight refresh, shared by every request that gets a 401.
 *
 * Without this, an app that loads six queries on mount fires six refreshes at
 * once. Each rotates the refresh token, so five of them present a token that has
 * just been superseded — and rotation treats a superseded token as theft and
 * revokes the session. The user would be signed out by their own page load.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so concurrent callers all observe the same
      // promise, but the next 401 after this one starts a fresh attempt.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

/**
 * Wraps the base query so every failure reaches a component in one shape, and a
 * expired access token is renewed once without the user noticing (S02: "silent
 * token refresh; a failed refresh re-authenticates and preserves the destination").
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, NormalizedError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  const url = typeof args === 'string' ? args : args.url;
  const refreshable = !NO_REFRESH.some((path) => url.includes(path));

  if (result.error?.status === 401 && refreshable) {
    if (await refreshSession()) {
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      // The session is genuinely gone. Send the user to sign in, preserving where
      // they were so they land back there rather than on the dashboard.
      redirectToLogin();
    }
  }

  if (result.error) {
    return { error: normalizeError(result.error) };
  }
  return { data: result.data };
};

/** Full-page navigation, because the login flow leaves the SPA entirely. */
function redirectToLogin(): void {
  if (window.location.pathname.startsWith(LOGIN_ROUTE)) return;
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(loginPath(returnTo));
}

/** Exported for unit testing — it is pure, and it is what every error state renders. */
export function normalizeError(error: FetchBaseQueryError): NormalizedError {
  // A transport failure — offline, DNS, aborted. No envelope to read.
  if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') {
    return {
      status: error.status,
      code: 'NETWORK_ERROR',
      message: 'Could not reach the server. Check your connection and try again.',
    };
  }

  if (error.status === 'PARSING_ERROR' || error.status === 'CUSTOM_ERROR') {
    return {
      status: error.status,
      code: 'UNEXPECTED_RESPONSE',
      message: 'The server returned something unexpected.',
    };
  }

  // An HTTP error. The API always sends the ApiError envelope, but a proxy or a
  // gateway might not, so the shape is checked rather than assumed.
  const body = error.data as Partial<ApiError> | undefined;
  return {
    status: error.status,
    code: body?.code ?? 'REQUEST_FAILED',
    message: body?.message ?? 'The request failed.',
    ...(body?.details ? { details: body.details } : {}),
    ...(body?.requestId ? { requestId: body.requestId } : {}),
  };
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: API_TAGS,
  // Refetch on reconnect so the console recovers on its own after a network blip
  // rather than sitting on stale data behind a "connected" indicator.
  refetchOnReconnect: true,
  endpoints: () => ({}),
});
